import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { loadEmployees, Employee, getEmployeeDisplayLabel, isManagerInHierarchy } from '../../utils/employeeData';
import { useNotification } from '../NotificationContext';
import apiClient from '../../utils/api';

interface BasicDetailsData {
  estimatedContractAmount: string;
  needsContractorAgreement: boolean;
  requestorName: string;
  requestorCode?: string;
  requestorEmail?: string;
  requestorManager: string;
  requestorManagerCode?: string;
  requestorManagerEmail?: string;
  companyName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
  phone: string;
  contactPerson: string;
  contactEmail: string;
  vendorWebsite: string;
  howLearnedAbout: string;
  goodsServicesDescription: string;
}

interface BasicDetailsFormProps {
  data: BasicDetailsData;
  onChange: (data: BasicDetailsData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
  w9FilePath?: string;
  w9FileName?: string;
  formId?: string;
  requiresIronclad?: boolean;
  onW9Upload?: (path: string, filename: string) => void;
}

const BasicDetailsForm: React.FC<BasicDetailsFormProps> = ({ 
  data, 
  onChange, 
  disabled = false, 
  errors = [],
  w9FilePath,
  w9FileName,
  formId,
  requiresIronclad = false,
  onW9Upload
}) => {
  const { showNotification } = useNotification();
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [managerError, setManagerError] = useState<string>('');
  const [uploadingW9, setUploadingW9] = useState(false);
  const [viewPdfOpen, setViewPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [deletingW9, setDeletingW9] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const employeeData = await loadEmployees();
      setEmployees(employeeData);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    // Validate manager hierarchy whenever requestor or manager changes
    if (data.requestorCode && data.requestorManagerCode && employees.length > 0) {
      const isValid = isManagerInHierarchy(employees, data.requestorCode, data.requestorManagerCode);
      if (!isValid) {
        setManagerError('Requestor\'s Manager doesn\'t follow hierarchical structure. Please verify.');
      } else {
        setManagerError('');
      }
    } else {
      setManagerError('');
    }
  }, [data.requestorCode, data.requestorManagerCode, employees]);

  const handleChange = (field: keyof BasicDetailsData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  const handleEmployeeChange = (field: 'requestorName' | 'requestorManager', employee: Employee | null) => {
    if (field === 'requestorName') {
      onChange({
        ...data,
        requestorName: employee ? employee.employeeName : '',
        requestorCode: employee ? employee.employeeCode : '',
        requestorEmail: employee ? employee.workEmail : '',
      });
    } else {
      onChange({
        ...data,
        requestorManager: employee ? employee.employeeName : '',
        requestorManagerCode: employee ? employee.employeeCode : '',
        requestorManagerEmail: employee ? employee.workEmail : '',
      });
    }
  };

  // Filter out the requestor from manager options
  const managerOptions = employees.filter(emp => emp.employeeCode !== data.requestorCode);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            required
            fullWidth
            label="Estimated Total Contract Value (USD)"
            value={data.estimatedContractAmount}
            onChange={handleChange('estimatedContractAmount')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('estimatedContractAmount')}
            helperText={getFieldError('estimatedContractAmount')}
          />
        </Box>

        <FormControl component="fieldset" disabled={disabled} error={!!getFieldError('needsContractorAgreement')}>
          <FormLabel component="legend">
            Do you need a Contractor/Consultant Agreement drafted?
          </FormLabel>
          <RadioGroup
            value={data.needsContractorAgreement ? 'true' : 'false'}
            onChange={(e) => {
              onChange({
                ...data,
                needsContractorAgreement: e.target.value === 'true',
              });
            }}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
          {getFieldError('needsContractorAgreement') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('needsContractorAgreement')}
            </Typography>
          )}
        </FormControl>

        {/* W9 Upload Section */}
        {formId && (
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: requiresIronclad ? '#fff8e1' : '#f5f5f5' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              W9 Form {requiresIronclad ? '(Required for Ironclad Workflow)' : '(Optional)'}
            </Typography>
            {w9FilePath ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="success.main">
                  ✓ {w9FileName || 'W9 uploaded'}
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const response = await apiClient.get(`/api/w9/view/${formId}`, {
                        responseType: 'blob'
                      });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      setPdfUrl(url);
                      setViewPdfOpen(true);
                    } catch (error: any) {
                      console.error('Error viewing W9:', error);
                      showNotification(error.response?.data?.error || 'Failed to view W9', 'error');
                    }
                  }}
                >
                  View
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const response = await apiClient.get(`/api/w9/download/${formId}`, {
                        responseType: 'blob'
                      });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `W9_${w9FileName || 'document'}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      showNotification('W9 downloaded successfully', 'success');
                    } catch (error: any) {
                      console.error('Error downloading W9:', error);
                      showNotification(error.response?.data?.error || 'Failed to download W9', 'error');
                    }
                  }}
                >
                  Download
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={disabled || deletingW9}
                  startIcon={deletingW9 ? <CircularProgress size={16} /> : <DeleteIcon />}
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete this W9 form? This action cannot be undone.')) {
                      return;
                    }
                    setDeletingW9(true);
                    try {
                      const response = await apiClient.delete(`/api/w9/${formId}`);
                      if (response.data.success) {
                        if (onW9Upload) {
                          onW9Upload('', '');
                        }
                        showNotification('W9 deleted successfully', 'success');
                      } else {
                        showNotification(response.data.error || 'Failed to delete W9', 'error');
                      }
                    } catch (error: any) {
                      console.error('Error deleting W9:', error);
                      showNotification(error.response?.data?.error || 'Failed to delete W9', 'error');
                    } finally {
                      setDeletingW9(false);
                    }
                  }}
                >
                  {deletingW9 ? 'Deleting...' : 'Delete'}
                </Button>
              </Box>
            ) : (
              <Box>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="w9-upload-basic-details"
                  type="file"
                  disabled={disabled || uploadingW9}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file && onW9Upload) {
                      if (file.size > 5 * 1024 * 1024) {
                        showNotification('W9 file size must be less than 5MB', 'error');
                        return;
                      }

                      const formDataUpload = new FormData();
                      formDataUpload.append('w9', file);
                      
                      setUploadingW9(true);
                      try {
                        const response = await apiClient.post(`/api/w9/upload/${formId}`, formDataUpload, {
                          headers: {
                            'Content-Type': 'multipart/form-data',
                          },
                        });
                        
                        if (response.data.success) {
                          onW9Upload(response.data.data.path, response.data.data.filename);
                          showNotification('W9 uploaded successfully! ✓', 'success');
                        } else {
                          const errorMsg = response.data.error || 'Failed to upload W9';
                          showNotification(errorMsg, 'error');
                        }
                      } catch (error: any) {
                        console.error('=== W9 Upload Error Details ===');
                        console.error('Full Error Object:', error);
                        console.error('Error Message:', error.message);
                        console.error('Error Code:', error.code);
                        console.error('Response Status:', error.response?.status);
                        console.error('Response Data:', error.response?.data);
                        console.error('Response Headers:', error.response?.headers);
                        console.error('Request URL:', error.config?.url);
                        console.error('Request Method:', error.config?.method);
                        console.error('================================');
                        const errorMsg = error.response?.data?.error || 'Network error: Unable to upload W9. Please try again.';
                        showNotification(errorMsg, 'error');
                      } finally {
                        setUploadingW9(false);
                        // Clear the input so the same file can be re-uploaded if needed
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <label htmlFor="w9-upload-basic-details">
                  <Button 
                    variant="outlined" 
                    component="span" 
                    size="small" 
                    disabled={disabled || uploadingW9}
                    startIcon={uploadingW9 ? <CircularProgress size={16} /> : null}
                  >
                    {uploadingW9 ? 'Uploading...' : 'Upload W9 (PDF)'}
                  </Button>
                </label>
                {requiresIronclad && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    W9 is required for Ironclad contractor agreements
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        <Autocomplete
          fullWidth
          options={employees}
          getOptionLabel={(employee) => getEmployeeDisplayLabel(employee)}
          value={employees.find(emp => emp.employeeCode === data.requestorCode) || null}
          onChange={(_, newValue) => handleEmployeeChange('requestorName', newValue)}
          loading={loading}
          disabled={true}
          readOnly={true}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Requestor Name (Auto-filled)"
              placeholder="Auto-filled with your name"
              InputProps={{
                ...params.InputProps,
                readOnly: true,
              }}
              helperText={getFieldError('requestorName') || "This field is locked to the logged-in user"}
              error={!!getFieldError('requestorName')}
            />
          )}
          isOptionEqualToValue={(option, value) => option.employeeCode === value.employeeCode}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    rootBoundary: 'document',
                  },
                },
              ],
            },
          }}
        />

        {managerError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {managerError}
          </Alert>
        )}

        <Autocomplete
          fullWidth
          disablePortal
          options={managerOptions}
          getOptionLabel={(employee) => getEmployeeDisplayLabel(employee)}
          value={managerOptions.find(emp => emp.employeeCode === data.requestorManagerCode) || null}
          onChange={(_, newValue) => handleEmployeeChange('requestorManager', newValue)}
          loading={loading}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Approving Manager"
              variant="outlined"
              helperText={getFieldError('requestorManager') || 'Select the appropriate approver for this vendor (requestor cannot approve their own submission)'}
              error={!!getFieldError('requestorManager') || !!managerError}
            />
          )}
          isOptionEqualToValue={(option, value) => option.employeeCode === value.employeeCode}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    rootBoundary: 'document',
                  },
                },
              ],
            },
          }}
        />

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
          New Vendor Information
        </Typography>

        <TextField
          required
          fullWidth
          label="Company Name"
          value={data.companyName}
          onChange={handleChange('companyName')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('companyName')}
          helperText={getFieldError('companyName')}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Address Information
        </Typography>

        <TextField
          required
          fullWidth
          label="Street Name and Number"
          value={data.streetAddress}
          onChange={handleChange('streetAddress')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('streetAddress')}
          helperText={getFieldError('streetAddress')}
        />

        <TextField
          fullWidth
          label="Address Line 2 (if needed)"
          value={data.addressLine2}
          onChange={handleChange('addressLine2')}
          variant="outlined"
          disabled={disabled}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            required
            fullWidth
            label="City"
            value={data.city}
            onChange={handleChange('city')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('city')}
            helperText={getFieldError('city')}
          />
          <TextField
            required
            fullWidth
            label="State/Province"
            value={data.state}
            onChange={handleChange('state')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('state')}
            helperText={getFieldError('state')}
          />
          <TextField
            required
            fullWidth
            label="ZIP (Postal) Code"
            value={data.zipCode}
            onChange={handleChange('zipCode')}
            variant="outlined"
            disabled={disabled}
            error={!!getFieldError('zipCode')}
            helperText={getFieldError('zipCode')}
          />
        </Box>

        <TextField
          required
          fullWidth
          label="Country"
          value={data.country}
          onChange={handleChange('country')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('country')}
          helperText={getFieldError('country')}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Contact Information
        </Typography>

        <TextField
          required
          fullWidth
          label="Phone"
          value={data.phone}
          onChange={handleChange('phone')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('phone')}
          helperText={getFieldError('phone')}
        />

        <TextField
          required
          fullWidth
          label="Contact Person"
          value={data.contactPerson}
          onChange={handleChange('contactPerson')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('contactPerson')}
          helperText={getFieldError('contactPerson')}
        />

        <TextField
          required
          fullWidth
          type="email"
          label="Contact Email Address"
          value={data.contactEmail}
          onChange={handleChange('contactEmail')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('contactEmail')}
          helperText={getFieldError('contactEmail')}
        />

        {/* Accounting-only fields - show when amount >= $1k */}
        {parseFloat(data.estimatedContractAmount) >= 1000 && (
          <>
            <TextField
              required
              fullWidth
              label="Vendor Website or N/A (if not applicable)"
              value={data.vendorWebsite}
              onChange={handleChange('vendorWebsite')}
              variant="outlined"
              disabled={disabled}
              placeholder="https://example.com"
              error={!!getFieldError('vendorWebsite')}
              helperText={getFieldError('vendorWebsite')}
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Additional Information
            </Typography>

            <TextField
              required
              fullWidth
              multiline
              rows={3}
              label="How did you learn about this vendor?"
              value={data.howLearnedAbout}
              onChange={handleChange('howLearnedAbout')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('howLearnedAbout')}
              helperText={getFieldError('howLearnedAbout')}
            />
          </>
        )}

        <TextField
          required
          fullWidth
          multiline
          rows={3}
          label="What goods or services will this vendor provide?"
          value={data.goodsServicesDescription}
          onChange={handleChange('goodsServicesDescription')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('goodsServicesDescription')}
          helperText={getFieldError('goodsServicesDescription')}
        />

        {/* PDF Viewer Modal */}
        <Dialog
          open={viewPdfOpen}
          onClose={() => {
            setViewPdfOpen(false);
            if (pdfUrl) {
              window.URL.revokeObjectURL(pdfUrl);
              setPdfUrl('');
            }
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            W9 Form - {w9FileName || 'Document'}
            <IconButton
              aria-label="close"
              onClick={() => {
                setViewPdfOpen(false);
                if (pdfUrl) {
                  window.URL.revokeObjectURL(pdfUrl);
                  setPdfUrl('');
                }
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {pdfUrl && (
              <Box sx={{ width: '100%', height: '70vh' }}>
                <iframe
                  src={pdfUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="W9 PDF Viewer"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setViewPdfOpen(false);
                if (pdfUrl) {
                  window.URL.revokeObjectURL(pdfUrl);
                  setPdfUrl('');
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default BasicDetailsForm;
